// src/providers/MsalAuthProvider.tsx
import React, { ReactNode, useEffect } from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { 
  AuthenticationResult, 
  AccountInfo,
  EventMessage,
  AuthError
} from "@azure/msal-browser";
import { useAuthStore } from "../stores/authStore";
import { apiLoginRequest, graphLoginRequest } from "../config/authConfig";
import { User } from "../types/auth";
import { msalInstance } from "../utils/msalInit";


msalInstance.addEventCallback((event: EventMessage) => {
  if (event.error) {
    console.error('MSAL Event Error:', event.eventType);
  }
});

interface MsalAuthProviderProps {
  children: ReactNode;
}

interface TokenClaims {
  roles?: string[];
  groups?: string[] | string;
  name?: string;
  [key: string]: unknown;
}

// Helper to extract roles from ID token claims
const getRolesFromClaims = (claims: TokenClaims): string[] => {
  const roles = claims?.roles || [];
  return roles;
};

// Map Azure AD roles to application roles 
type AppRole = 'admin' | 'user';

// Convert Microsoft Entra account to your User model
const mapAccountToUser = (account: AccountInfo): User => {
  const claims = (account.idTokenClaims || {}) as TokenClaims;
  
  const azureRoles = getRolesFromClaims(claims);
  
  // Map Azure AD roles to application roles
  let appRole: AppRole = "user";
  
  if (azureRoles.includes('TIL.PMP.ADMIN')) {
    appRole = "admin";
  } 
  else if (azureRoles.includes('TIL.PMP.USER')) {
    appRole = "user";
  }
  
  const groups = claims.groups || [];
  const groupsArray = Array.isArray(groups) ? groups : [groups];
  
  const user: User = {
    id: account.localAccountId,
    email: account.username,
    name: claims.name || account.username,
    role: appRole,
    allGroups: groupsArray,
    roles: azureRoles,
  };
  
  return user;
};

// Inner component that handles auth state
const MsalAuthHandler: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  
  const { setAuth, clearAuth } = useAuthStore();

  // Handle initial and token refresh authentication
  useEffect(() => {
    const handleTokenResponse = (apiResponse: AuthenticationResult, graphResponse?: AuthenticationResult): void => {
      if (apiResponse && apiResponse.account) {
        const user = mapAccountToUser(apiResponse.account);

        // Uncomment for testing purposes to get API token
        // console.log('API Token for testing:', apiResponse.accessToken);

        setAuth(user, apiResponse.accessToken, graphResponse?.accessToken);
      } else {
        console.error("Token response missing account information");
      }
    };

    const handleLogin = async (): Promise<void> => {
      // If no accounts, we're not authenticated
      if (accounts.length === 0) {
        clearAuth();
        return;
      }

      try {
        const apiResponse = await instance.acquireTokenSilent({
          ...apiLoginRequest,
          account: accounts[0],
        });

        const graphResponse = await instance.acquireTokenSilent({
          ...graphLoginRequest,
          account: accounts[0],
        });

        // Use the API token for authentication
        handleTokenResponse(apiResponse, graphResponse);
      } catch (error) {
        const authError = error as AuthError;
        console.error("Token acquisition failed");
        
        // Only clear auth state for specific errors
        if (authError.errorCode === 'user_cancelled' || 
            authError.errorCode === 'interaction_required' ||
            authError.errorCode === 'login_required') {
          clearAuth();
        }
      }
    };

    // Don't attempt login while another login operation is in progress
    if (inProgress === "none") {
      void handleLogin();
    }

    // Token refresh timer - check every 15 minutes
    const tokenRefreshInterval = setInterval(async () => {
      if (accounts.length > 0 && inProgress === "none") {
        try {
          const apiResponse = await instance.acquireTokenSilent({
            ...apiLoginRequest,
            account: accounts[0],
            forceRefresh: true,
          });

          const graphResponse = await instance.acquireTokenSilent({
            ...graphLoginRequest,
            account: accounts[0],
            forceRefresh: true,
          });

          handleTokenResponse(apiResponse, graphResponse);
        } catch (error) {
          const authError = error as AuthError;
          console.error("Token refresh failed");
          
          // Only clear auth state for specific errors
          if (authError.errorCode === 'user_cancelled' || 
              authError.errorCode === 'interaction_required' ||
              authError.errorCode === 'login_required') {
            clearAuth();
          }
        }
      }
    }, 15 * 60 * 1000); // Changed from 5 minutes to 15 minutes

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, [accounts, inProgress, instance, setAuth, clearAuth]);

  return <>{children}</>;
};

// Main Provider component
export const MsalAuthProvider: React.FC<MsalAuthProviderProps> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthHandler>{children}</MsalAuthHandler>
    </MsalProvider>
  );
};